import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { 
  getGoalById,
  updateGoalDraft,
  saveGoalEdits
} from '../../services/goalsService';
import { Goal, GoalType, GoalDocument } from '../../types';
import { ArrowLeft, AlertCircle, Calendar, FileEdit, Send } from 'lucide-react';

const goalTypeLabels: Record<GoalType, string> = {
  normal: 'Normal Goal',
  stretch: 'Stretch Goal',
  hard: 'Hard Goal',
};

const goalTypeColors: Record<GoalType, string> = {
  normal: 'bg-blue-100 text-blue-700',
  stretch: 'bg-yellow-100 text-yellow-700',
  hard: 'bg-red-100 text-red-700',
};

const EditGoals = () => {
  const { goalId } = useParams<{ goalId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [goalDoc, setGoalDoc] = useState<GoalDocument | null>(null);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGoal = async () => {
      if (!goalId) return;
      
      try {
        const doc = await getGoalById(goalId);
        if (doc) {
          setGoalDoc(doc);
          setGoals(doc.goals);
        } else {
          setError('Goal not found');
        }
      } catch (err) {
        console.error('Error fetching goal:', err);
        setError('Failed to load goal');
      } finally {
        setLoading(false);
      }
    };

    fetchGoal();
  }, [goalId]);

  const updateGoal = (index: number, field: keyof Goal, value: string) => {
    const newGoals = [...goals];
    newGoals[index] = { ...newGoals[index], [field]: value };
    setGoals(newGoals);
  };

  const validateGoals = (): boolean => {
    for (let i = 0; i < goals.length; i++) {
      if (!goals[i].title.trim()) {
        setError(`Please enter a title for Goal ${i + 1}`);
        return false;
      }
      if (!goals[i].description.trim()) {
        setError(`Please enter a description for Goal ${i + 1}`);
        return false;
      }
    }
    return true;
  };

  const handleSaveDraft = async () => {
    setError(null);
    if (!goalId || !user) return;

    setSaving(true);
    
    try {
      await updateGoalDraft(goalId, goals, user.displayName, false);
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Error saving draft:', err);
      setError(err.message || 'Failed to save draft. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    if (!goalId || !user || !goalDoc) return;
    
    if (!validateGoals()) return;

    setSubmitting(true);
    
    try {
      if (goalDoc.status !== 'draft' && goalDoc.allowEdit) {
        // Editing a submitted goal - save edits and turn off allowEdit
        await saveGoalEdits(goalId, goals);
      } else {
        // Submitting a draft
        await updateGoalDraft(goalId, goals, user.displayName, true);
      }
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Error submitting goals:', err);
      setError(err.message || 'Failed to submit goals. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cps-primary"></div>
      </div>
    );
  }

  if (!goalDoc) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-red-800 mb-2">Goal Not Found</h2>
          <p className="text-red-700 mb-4">{error || 'The requested goal could not be found.'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-red-600 hover:text-red-800 font-medium"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (goalDoc.status !== 'draft' && !goalDoc.allowEdit) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-yellow-800 mb-2">Cannot Edit</h2>
          <p className="text-yellow-700 mb-4">
            This goal has already been submitted and cannot be edited.
          </p>
          <button
            onClick={() => navigate(`/goals/${goalId}`)}
            className="text-yellow-600 hover:text-yellow-800 font-medium"
          >
            View Goal →
          </button>
        </div>
      </div>
    );
  }

  const isEditingSubmitted = goalDoc.status !== 'draft' && goalDoc.allowEdit;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditingSubmitted ? 'Edit Goals' : 'Edit Draft Goals'}
          </h1>
          <p className="text-gray-500">
            {isEditingSubmitted ? 'Your manager has allowed you to make changes' : 'Continue working on your goals'}
          </p>
        </div>
      </div>

      <div className="bg-gray-100 rounded-lg px-4 py-2 mb-6 inline-flex items-center gap-2">
        <Calendar className="w-4 h-4 text-gray-600" />
        <span className="text-gray-700 font-medium">
          Q{goalDoc.quarter} {goalDoc.year}
        </span>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
          isEditingSubmitted ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'
        }`}>
          {isEditingSubmitted ? 'Edit Allowed' : 'Draft'}
        </span>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        {goals.map((goal, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xl font-bold text-gray-300">#{index + 1}</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${goalTypeColors[goal.type]}`}>
                {goalTypeLabels[goal.type]}
              </span>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Goal Title
                </label>
                <input
                  type="text"
                  value={goal.title}
                  onChange={(e) => updateGoal(index, 'title', e.target.value)}
                  placeholder={`Enter title for ${goalTypeLabels[goal.type].toLowerCase()}`}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-cps-primary focus:border-cps-primary"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={goal.description}
                  onChange={(e) => updateGoal(index, 'description', e.target.value)}
                  placeholder="Clear outline of what would be required/steps to complete this goal"
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-cps-primary focus:border-cps-primary resize-none"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex justify-end gap-4">
        <button
          onClick={() => navigate('/dashboard')}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        {!isEditingSubmitted && (
          <button
            onClick={handleSaveDraft}
            disabled={saving || submitting}
            className="inline-flex items-center gap-2 px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></div>
                Saving...
              </>
            ) : (
              <>
                <FileEdit className="w-4 h-4" />
                Save Draft
              </>
            )}
          </button>
        )}
        <button
          onClick={handleSubmit}
          disabled={saving || submitting}
          className="inline-flex items-center gap-2 px-6 py-2 bg-cps-primary text-white rounded-lg hover:bg-cps-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              {isEditingSubmitted ? 'Saving...' : 'Submitting...'}
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              {isEditingSubmitted ? 'Save Changes' : 'Submit Goals'}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default EditGoals;